import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  Plus,
  Zap,
  RefreshCw,
  MessageSquare,
  Edit3,
  Trash2,
  Power,
} from "lucide-react";
import {
  listAutomations,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  listCannedReplies,
  createCannedReply,
  updateCannedReply,
  deleteCannedReply,
  type CurrentUser,
  type AutomationRule,
  type CannedReply,
} from "../../lib/api";

export function AutomationsView({ token, user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"bots" | "canned">("bots");
  const [isBotModalOpen, setIsBotModalOpen] = useState(false);
  const [isCannedModalOpen, setIsCannedModalOpen] = useState(false);

  // Form State for Keyword Bot
  const [ruleName, setRuleName] = useState("");
  const [triggerKeywords, setTriggerKeywords] = useState("");
  const [responseMessage, setResponseMessage] = useState("");

  // Form State for Canned Reply
  const [shortcut, setShortcut] = useState("");
  const [title, setTitle] = useState("");
  const [cannedContent, setCannedContent] = useState("");

  const [editingBot, setEditingBot] = useState<AutomationRule | null>(null);
  const [editBotName, setEditBotName] = useState("");
  const [editBotKeywords, setEditBotKeywords] = useState("");
  const [editBotResponse, setEditBotResponse] = useState("");

  // Edit State for Canned Reply
  const [editingCanned, setEditingCanned] = useState<CannedReply | null>(null);
  const [editCannedShortcut, setEditCannedShortcut] = useState("");
  const [editCannedTitle, setEditCannedTitle] = useState("");
  const [editCannedContent, setEditCannedContent] = useState("");

  // Live Automations Query
  const automationsQuery = useQuery({
    queryKey: ["automations", user.tenant_id],
    queryFn: () => listAutomations(token, user.tenant_id),
  });

  // Live Canned Replies Query
  const cannedQuery = useQuery({
    queryKey: ["inbox", "canned-replies", user.tenant_id],
    queryFn: () => listCannedReplies(token, user.tenant_id),
  });

  const automations = automationsQuery.data || [];
  const cannedReplies = cannedQuery.data || [];

  // Create Bot Mutation
  const createBotMutation = useMutation({
    mutationFn: () =>
      createAutomation(
        token,
        {
          name: ruleName.trim(),
          trigger_type: "keyword_match",
          keywords: triggerKeywords.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean),
          action_type: "send_message",
          action_payload: { text: responseMessage.trim() },
          is_active: true,
        },
        user.tenant_id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      setIsBotModalOpen(false);
      setRuleName("");
      setTriggerKeywords("");
      setResponseMessage("");
    },
    onError: (err: any) => {
      alert(`Failed to create bot: ${err.message || "Unknown error"}`);
    },
  });

  // Update Bot Mutation
  const updateBotMutation = useMutation({
    mutationFn: () => {
      if (!editingBot) throw new Error("No bot selected");
      return updateAutomation(token, editingBot.id, {
        name: editBotName.trim(),
        keywords: editBotKeywords.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean),
        action_payload: { text: editBotResponse.trim() },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["automations"] });
      setEditingBot(null);
    },
    onError: (err: any) => {
      alert(`Failed to update bot: ${err.message || "Unknown error"}`);
    },
  });

  // Toggle Bot Mutation
  const toggleBotMutation = useMutation({
    mutationFn: ({ botId, active }: { botId: string; active: boolean }) =>
      updateAutomation(token, botId, { is_active: !active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automations"] }),
    onError: (err: any) => {
      alert(`Failed to toggle bot: ${err.message || "Unknown error"}`);
    },
  });

  // Delete Bot Mutation
  const deleteBotMutation = useMutation({
    mutationFn: (botId: string) => deleteAutomation(token, botId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["automations"] }),
    onError: (err: any) => {
      alert(`Failed to delete bot: ${err.message || "Unknown error"}`);
    },
  });

  // Create Canned Reply Mutation
  const createCannedMutation = useMutation({
    mutationFn: () =>
      createCannedReply(
        token,
        {
          shortcut: shortcut.trim().replace(/^\//, ""),
          title: title.trim(),
          content: cannedContent.trim(),
        },
        user.tenant_id
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inbox", "canned-replies"] });
      setIsCannedModalOpen(false);
      setShortcut("");
      setTitle("");
      setCannedContent("");
    },
    onError: (err: any) => {
      alert(`Failed to create canned reply: ${err.message || "Unknown error"}`);
    },
  });

  // Update Canned Reply Mutation
  const updateCannedMutation = useMutation({
    mutationFn: () => {
      if (!editingCanned) throw new Error("No canned reply selected");
      return updateCannedReply(token, editingCanned.id, {
        shortcut: editCannedShortcut.trim().replace(/^\//, ""),
        title: editCannedTitle.trim(),
        content: editCannedContent.trim(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inbox", "canned-replies"] });
      setEditingCanned(null);
    },
    onError: (err: any) => {
      alert(`Failed to update canned reply: ${err.message || "Unknown error"}`);
    },
  });

  // Delete Canned Reply Mutation
  const deleteCannedMutation = useMutation({
    mutationFn: (replyId: string) => deleteCannedReply(token, replyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["inbox", "canned-replies"] }),
    onError: (err: any) => {
      alert(`Failed to delete canned reply: ${err.message || "Unknown error"}`);
    },
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Bot className="w-6 h-6 text-emerald-600" />
            Bots &amp; Smart Automations
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Build 24/7 keyword auto-responders and standard canned replies for live agent messaging.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center p-1 rounded-lg bg-slate-100 text-xs">
          <button
            onClick={() => setActiveTab("bots")}
            className={`px-3 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "bots" ? "bg-white text-emerald-600 shadow-2xs" : "text-slate-600"
            }`}
          >
            Keyword Bots ({automations.length})
          </button>
          <button
            onClick={() => setActiveTab("canned")}
            className={`px-3 py-1.5 rounded-md font-semibold transition-all ${
              activeTab === "canned" ? "bg-white text-emerald-600 shadow-2xs" : "text-slate-600"
            }`}
          >
            Canned Replies ({cannedReplies.length})
          </button>
        </div>
      </div>

      {/* Action Toolbar */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {activeTab === "bots" ? (
            <button
              onClick={() => setIsBotModalOpen(true)}
              className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New Keyword Auto-Responder</span>
            </button>
          ) : (
            <button
              onClick={() => setIsCannedModalOpen(true)}
              className="btn-primary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New Canned Reply</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              automationsQuery.refetch();
              cannedQuery.refetch();
            }}
            className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs"
          >
            <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
            <span>Sync</span>
          </button>
        </div>
      </div>

      {/* Tab 1: Keyword Bots */}
      {activeTab === "bots" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {automationsQuery.isLoading ? (
            <div className="col-span-3 p-16 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl shadow-2xs">
              <div className="flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                <span className="font-semibold text-slate-600">Loading bot automation rules...</span>
                <span className="text-[11px] text-slate-400">Fetching live trigger keywords and auto-reply dispatchers</span>
              </div>
            </div>
          ) : automations.length === 0 ? (
            <div className="col-span-3 p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
              <Bot className="w-12 h-12 text-slate-400 mx-auto" />
              <h3 className="text-sm font-bold text-slate-900">No keyword bots active</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Set up automated replies that instantly trigger whenever a customer messages specific keywords like `price`, `help`, or `demo`.
              </p>
              <button
                onClick={() => setIsBotModalOpen(true)}
                className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create Auto-Responder</span>
              </button>
            </div>
          ) : (
            automations.map((bot: AutomationRule) => (
              <div
                key={bot.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{bot.name}</h3>
                    <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-semibold border border-emerald-200">
                      Active
                    </span>
                  </div>
                  <Zap className="w-4 h-4 text-amber-500" />
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-2">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400">Trigger Keywords:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {bot.keywords.map((kw, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 rounded bg-white text-slate-800 font-mono text-[10px] border border-slate-200 font-bold"
                        >
                          "{kw}"
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-200/60">
                    <span className="text-[10px] uppercase font-bold text-slate-400">Bot Response:</span>
                    <p className="text-slate-700 mt-0.5 line-clamp-2">
                      {bot.action_payload?.text || "Automated response"}
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
                  <button
                    onClick={() => toggleBotMutation.mutate({ botId: bot.id, active: bot.is_active })}
                    className={`text-[11px] font-semibold px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                      bot.is_active
                        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    <Power className="w-3 h-3" />
                    <span>{bot.is_active ? "Active" : "Disabled"}</span>
                  </button>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingBot(bot);
                        setEditBotName(bot.name);
                        setEditBotKeywords(bot.keywords.join(", "));
                        setEditBotResponse(bot.action_payload?.text || "");
                      }}
                      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                      title="Edit Bot Rule"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => {
                        if (confirm(`Delete bot rule "${bot.name}"?`)) {
                          deleteBotMutation.mutate(bot.id);
                        }
                      }}
                      disabled={deleteBotMutation.isPending}
                      className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                      title="Delete Bot Rule"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab 2: Canned Replies */}
      {activeTab === "canned" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cannedQuery.isLoading ? (
            <div className="col-span-3 p-16 text-center text-slate-400 text-xs bg-white border border-slate-200 rounded-xl shadow-2xs">
              <div className="flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
                <span className="font-semibold text-slate-600">Loading canned replies...</span>
                <span className="text-[11px] text-slate-400">Fetching keyboard shortcuts and fast canned response templates</span>
              </div>
            </div>
          ) : cannedReplies.length === 0 ? (
            <div className="col-span-3 p-12 text-center bg-white border border-slate-200 rounded-xl shadow-2xs space-y-3">
              <MessageSquare className="w-12 h-12 text-slate-400 mx-auto" />
              <h3 className="text-sm font-bold text-slate-900">No canned replies created</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Create quick reply templates that support agents can insert into live chat using shortcuts like `/welcome` or `/hours`.
              </p>
              <button
                onClick={() => setIsCannedModalOpen(true)}
                className="btn-primary text-xs py-2 px-3.5 mx-auto inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create Canned Reply</span>
              </button>
            </div>
          ) : (
            cannedReplies.map((reply: CannedReply) => (
              <div
                key={reply.id}
                className="bg-white border border-slate-200 rounded-xl p-5 shadow-2xs space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">{reply.title}</h3>
                    <span className="font-mono text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 mt-1 inline-block">
                      /{reply.shortcut}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingCanned(reply);
                        setEditCannedShortcut(reply.shortcut);
                        setEditCannedTitle(reply.title);
                        setEditCannedContent(reply.content);
                      }}
                      className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                      title="Edit Canned Reply"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete canned reply "/${reply.shortcut}"?`)) {
                          deleteCannedMutation.mutate(reply.id);
                        }
                      }}
                      className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                      title="Delete Canned Reply"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-700 leading-relaxed">
                  {reply.content}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* New Keyword Bot Modal */}
      {isBotModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsBotModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Bot className="w-4 h-4 text-emerald-600" />
                Create Keyword Auto-Responder
              </h3>
              <button onClick={() => setIsBotModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Bot Rule Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Price Inquiry Auto-Responder"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  className="modern-input w-full text-xs"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Trigger Keywords (comma separated) *
                </label>
                <input
                  type="text"
                  placeholder="price, cost, plan, pricing"
                  value={triggerKeywords}
                  onChange={(e) => setTriggerKeywords(e.target.value)}
                  className="modern-input w-full text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Automated Reply Text *
                </label>
                <textarea
                  placeholder="Hello! Our pricing starts at $49/mo. Visit our pricing page at https://wamaster.io/pricing or type 'agent' to speak with sales."
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  className="modern-input w-full text-xs h-24"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary text-xs" onClick={() => setIsBotModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createBotMutation.mutate()}
                  disabled={!ruleName.trim() || !triggerKeywords.trim() || !responseMessage.trim() || createBotMutation.isPending}
                >
                  {createBotMutation.isPending ? "Creating..." : "Save Bot Rule"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Keyword Bot Modal */}
      {editingBot && (
        <div className="modal-backdrop" onClick={() => setEditingBot(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-emerald-600" />
                Edit Keyword Bot Rule
              </h3>
              <button onClick={() => setEditingBot(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Rule Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Pricing Inquiries"
                  value={editBotName}
                  onChange={(e) => setEditBotName(e.target.value)}
                  className="modern-input w-full text-xs"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Trigger Keywords (comma separated) *
                </label>
                <input
                  type="text"
                  placeholder="price, cost, plan, pricing"
                  value={editBotKeywords}
                  onChange={(e) => setEditBotKeywords(e.target.value)}
                  className="modern-input w-full text-xs font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Automated Reply Text *
                </label>
                <textarea
                  placeholder="Hello! Our pricing starts at $49/mo..."
                  value={editBotResponse}
                  onChange={(e) => setEditBotResponse(e.target.value)}
                  className="modern-input w-full text-xs h-24"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setEditingBot(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => updateBotMutation.mutate()}
                  disabled={!editBotName.trim() || !editBotKeywords.trim() || !editBotResponse.trim() || updateBotMutation.isPending}
                >
                  {updateBotMutation.isPending ? "Updating..." : "Update Bot Rule"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Canned Reply Modal */}
      {isCannedModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsCannedModalOpen(false)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-emerald-600" />
                Create Canned Reply Template
              </h3>
              <button onClick={() => setIsCannedModalOpen(false)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Shortcut Keyword (e.g. "welcome") *
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-300 rounded-l-lg text-xs font-mono text-slate-600">
                    /
                  </span>
                  <input
                    type="text"
                    placeholder="welcome"
                    value={shortcut}
                    onChange={(e) => setShortcut(e.target.value)}
                    className="modern-input w-full rounded-l-none font-mono text-xs"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  placeholder="Welcome Greeting"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="modern-input w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Message Content *</label>
                <textarea
                  placeholder="Hi there! Welcome to WA Master support. How can we help you today?"
                  value={cannedContent}
                  onChange={(e) => setCannedContent(e.target.value)}
                  className="modern-input w-full text-xs h-24"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary text-xs" onClick={() => setIsCannedModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => createCannedMutation.mutate()}
                  disabled={!shortcut.trim() || !title.trim() || !cannedContent.trim() || createCannedMutation.isPending}
                >
                  {createCannedMutation.isPending ? "Creating..." : "Save Canned Reply"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Canned Reply Modal */}
      {editingCanned && (
        <div className="modal-backdrop" onClick={() => setEditingCanned(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 mb-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-emerald-600" />
                Edit Canned Reply Template
              </h3>
              <button onClick={() => setEditingCanned(null)} className="text-slate-400 hover:text-slate-700 text-xs font-bold">
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Shortcut Keyword (e.g. "welcome") *
                </label>
                <div className="flex items-center">
                  <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-300 rounded-l-lg text-xs font-mono text-slate-600">
                    /
                  </span>
                  <input
                    type="text"
                    placeholder="welcome"
                    value={editCannedShortcut}
                    onChange={(e) => setEditCannedShortcut(e.target.value)}
                    className="modern-input w-full rounded-l-none font-mono text-xs"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  placeholder="Welcome Greeting"
                  value={editCannedTitle}
                  onChange={(e) => setEditCannedTitle(e.target.value)}
                  className="modern-input w-full text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Message Content *</label>
                <textarea
                  placeholder="Hi there! Welcome to WA Master support. How can we help you today?"
                  value={editCannedContent}
                  onChange={(e) => setEditCannedContent(e.target.value)}
                  className="modern-input w-full text-xs h-24"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button className="btn-secondary text-xs" onClick={() => setEditingCanned(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary text-xs"
                  onClick={() => updateCannedMutation.mutate()}
                  disabled={!editCannedShortcut.trim() || !editCannedTitle.trim() || !editCannedContent.trim() || updateCannedMutation.isPending}
                >
                  {updateCannedMutation.isPending ? "Updating..." : "Update Canned Reply"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
